import { useMemo, useState } from 'react';
import { BROKERS, INSTRUMENTS, PRODUCT_USE_CASES } from '@/mocks/compare';
import { fmtMoney, marginFor } from '../calculations';
import { CompareBar, NumberField, SectionHeading, Tag } from './ui';

export default function ProductSection() {
  const [query, setQuery] = useState('');
  const [symbol, setSymbol] = useState('EURUSD');
  const [lot, setLot] = useState(1);

  const inst = INSTRUMENTS.find((i) => i.symbol === symbol) ?? INSTRUMENTS[0];

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return INSTRUMENTS;
    return INSTRUMENTS.filter((i) => i.symbol.toLowerCase().includes(q) || i.name.toLowerCase().includes(q));
  }, [query]);

  const margins = BROKERS.map((b) => ({ broker: b, margin: marginFor(inst, b, lot) }));
  const minMargin = Math.min(...margins.map((m) => m.margin));

  return (
    <section id="product" className="scroll-mt-24 rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
      <SectionHeading
        eyebrow="Kiểm tra thông số"
        title="Kiểm tra thông số sản phẩm"
        subtitle="Tra cứu thông số chi tiết của một sản phẩm giao dịch và so sánh số tiền ký quỹ giữa các nhà môi giới."
      />

      {/* Search */}
      <div className="mt-6">
        <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Tìm sản phẩm</span>
        <div className="relative">
          <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-base text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Nhập ký hiệu hoặc tên sản phẩm, ví dụ: EURUSD, Vàng, Bitcoin..."
            className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm font-semibold text-slate-800 outline-none transition focus:border-violet-400 focus:ring-4 focus:ring-violet-100"
          />
        </div>

        {/* Result chips */}
        <div className="mt-3 flex flex-wrap gap-2">
          {results.slice(0, 12).map((i) => {
            const active = i.symbol === inst.symbol;
            return (
              <button
                key={i.symbol}
                type="button"
                onClick={() => {
                  setSymbol(i.symbol);
                  setQuery('');
                }}
                className={`whitespace-nowrap rounded-full px-3 py-1.5 font-mono text-xs font-bold transition-colors ${
                  active
                    ? 'bg-violet-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {i.symbol}
              </button>
            );
          })}
          {results.length === 0 && <span className="text-xs text-slate-400">Không tìm thấy sản phẩm phù hợp.</span>}
        </div>
      </div>

      {/* Detail grid */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Spec card */}
        <div className="rounded-2xl border border-slate-200/80 bg-slate-50/50 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-mono text-lg font-bold text-slate-900">{inst.symbol}</p>
              <p className="text-xs text-slate-500">{inst.name}</p>
            </div>
            <Tag tone="violet">{inst.assetClass}</Tag>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3">
            <Spec label="Contract size" value={inst.contractSize.toLocaleString()} />
            <Spec label="Giá trị pip (1 lot)" value={`${inst.pipValue} USD`} />
            <Spec label="Khối lượng tối thiểu" value={`${inst.minLot} lot`} />
            <Spec label="Khối lượng tối đa" value={`${inst.maxLot} lot`} />
            <Spec label="Bước khối lượng" value={`${inst.lotStep} lot`} />
            <Spec label="Bước giá (Tick)" value={String(inst.tickSize)} />
            <Spec label="Swap Long" value={fmtMoney(inst.swapLong)} />
            <Spec label="Swap Short" value={fmtMoney(inst.swapShort)} />
            <Spec label="Giá tham chiếu" value={String(inst.refPrice)} />
            <Spec label="Giờ giao dịch" value={inst.tradingHours} />
          </div>
        </div>

        {/* Margin comparison */}
        <div className="rounded-2xl border border-slate-200/80 bg-slate-50/50 p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-base font-bold text-slate-900">So sánh ký quỹ</h3>
            <span className="font-mono text-[11px] text-slate-400">1:500 · 1:3000</span>
          </div>
          <div className="mt-4">
            <NumberField label="Khối lượng (lot)" value={lot} onChange={setLot} step={0.01} suffix="lot" />
          </div>
          <div className="mt-4 space-y-1.5">
            {margins.map(({ broker, margin }) => (
              <CompareBar
                key={broker.id}
                label={broker.name}
                value={margin}
                display={`${margin.toFixed(2)} USD`}
                max={Math.max(...margins.map((m) => m.margin))}
                color={broker.color}
                highlight={margin === minMargin}
              />
            ))}
          </div>
          <p className="mt-3 text-[11px] text-slate-400">
            Ký quỹ được tính theo công thức (Contract size × Giá) / Đòn bẩy. Sàn có đòn bẩy cao cần ít ký quỹ hơn.
          </p>
        </div>
      </div>

      {/* Use cases */}
      <div className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-3">
        {PRODUCT_USE_CASES.map((u) => (
          <div key={u.title} className="rounded-2xl border border-slate-200/80 bg-slate-50/50 p-4">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <i className={u.icon} />
            </span>
            <h4 className="mt-3 text-sm font-bold text-slate-800">{u.title}</h4>
            <p className="mt-1 text-xs leading-relaxed text-slate-500">{u.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Spec({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-slate-200/60 pb-2">
      <span className="text-xs text-slate-400">{label}</span>
      <span className="text-right font-mono text-xs font-semibold text-slate-700">{value}</span>
    </div>
  );
}